/**
 * Per-pillar tile shims. Each thin wrapper maps the pillar-specific template
 * shape (Connector / Knowledge / Model) onto the shared CatalogTile primitive
 * so each pillar keeps its own logo / category / tag conventions without
 * forcing a one-size-fits-all card.
 */

import type {
  UnifiedConnectorTpl,
  UnifiedKnowledgeTpl,
  UnifiedModelTpl,
} from '../../lib/api.ts';
import { BrandLogo } from '../../components/BrandLogo.tsx';
import { ConnectorLogo } from '../../components/ConnectorLogo.tsx';
import { formatLabel } from '../../lib/format.ts';

import { CatalogTile } from './CatalogTile.tsx';

interface ConnectorTileProps {
  tpl: UnifiedConnectorTpl;
  installing: boolean;
  canInstall: boolean;
  onInstall: () => void;
  onRequestAccess: () => void;
  onSuggestPriority: () => void;
  /** Global upvote count (migration 0051). */
  upvoteCount: number;
  isInstalled: boolean;
  /** Coming Soon "like" handler — one-click upvote, no modal. */
  onUpvote: () => void;
  hasUpvoted: boolean;
  /** Optional — coming-soon tiles intentionally pass undefined since
   *  their detail page isn't installable yet (the underlying record
   *  exists in the catalog table, but the user-facing record page is
   *  hidden until status flips to active). */
  onOpen?: () => void;
}

export function ConnectorTile({
  tpl, installing, canInstall, onInstall, onRequestAccess, onSuggestPriority, upvoteCount, isInstalled, onUpvote, hasUpvoted, onOpen,
}: ConnectorTileProps) {
  const comingSoon = tpl.status === 'coming_soon';
  return (
    <CatalogTile
      title={tpl.display_name}
      subtitle={tpl.description}
      logo={
        <ConnectorLogo
          slug={tpl.name}
          name={tpl.display_name}
          logoUrl={tpl.logo_url}
          size="md"
        />
      }
      comingSoon={comingSoon}
      canInstall={canInstall}
      installing={installing}
      onInstall={onInstall}
      onRequestAccess={onRequestAccess}
      onSuggestPriority={onSuggestPriority}
      upvoteCount={upvoteCount}
      isInstalled={isInstalled}
      onUpvote={onUpvote}
      hasUpvoted={hasUpvoted}
      {...(comingSoon || onOpen === undefined ? {} : { onOpenDetail: onOpen })}
    />
  );
}

interface KnowledgeTileProps {
  tpl: UnifiedKnowledgeTpl;
  installing: boolean;
  canInstall: boolean;
  onInstall: () => void;
  onRequestAccess: () => void;
  onSuggestPriority: () => void;
  /** Global upvote count (migration 0051). */
  upvoteCount: number;
  isInstalled: boolean;
  /** Coming Soon "like" handler — one-click upvote, no modal. */
  onUpvote: () => void;
  hasUpvoted: boolean;
  /** Push #31: drill-down handler — passed by Library, scoped by admin/user
   *  context. Not fired on comingSoon tiles (those upvote instead). */
  onOpen?: () => void;
}

export function KnowledgeTile({
  tpl, installing, canInstall, onInstall, onRequestAccess, onSuggestPriority, upvoteCount, isInstalled, onUpvote, hasUpvoted, onOpen,
}: KnowledgeTileProps) {
  const comingSoon = tpl.status === 'coming_soon';
  return (
    <CatalogTile
      title={tpl.display_name}
      subtitle={tpl.description}
      categoryLabel={formatLabel(tpl.category)}
      comingSoon={comingSoon}
      canInstall={canInstall}
      installing={installing}
      onInstall={onInstall}
      onRequestAccess={onRequestAccess}
      onSuggestPriority={onSuggestPriority}
      upvoteCount={upvoteCount}
      isInstalled={isInstalled}
      onUpvote={onUpvote}
      hasUpvoted={hasUpvoted}
      {...(comingSoon || onOpen === undefined ? {} : { onOpenDetail: onOpen })}
    />
  );
}

interface ModelTileProps {
  tpl: UnifiedModelTpl;
  installing: boolean;
  canInstall: boolean;
  onInstall: () => void;
  onRequestAccess: () => void;
  onSuggestPriority: () => void;
  /** Global upvote count (migration 0051). */
  upvoteCount: number;
  isInstalled: boolean;
  /** Coming Soon "like" handler — one-click upvote, no modal. */
  onUpvote: () => void;
  hasUpvoted: boolean;
  /** Push #31: drill-down handler. Not fired on comingSoon tiles. */
  onOpen?: () => void;
}

export function ModelTile({
  tpl, installing, canInstall, onInstall, onRequestAccess, onSuggestPriority, upvoteCount, isInstalled, onUpvote, hasUpvoted, onOpen,
}: ModelTileProps) {
  const comingSoon = tpl.status === 'coming_soon';
  // Push #75 — humanize the D-150 recommended_for slugs (lowercase
  // underscore-separated) into the uppercase short labels that match the
  // chat-rail tag conventions. "image_generation" → "IMAGE". "reasoning"
  // → "REASONING". Kept short so 3-4 tags fit on a card without overflow.
  const tagLabels: Record<string, string> = {
    reasoning: 'REASONING',
    fast: 'FAST',
    image_generation: 'IMAGE',
    code: 'CODE',
    cost_optimized: 'BUDGET',
    regulated: 'REGULATED',
  };
  const modelTags = (tpl.recommended_for ?? [])
    .map((t) => tagLabels[t] ?? t.toUpperCase())
    .slice(0, 4);
  return (
    <CatalogTile
      title={tpl.display_name}
      subtitle={tpl.description}
      logo={
        <BrandLogo
          slug={tpl.provider_name}
          name={tpl.provider_display_name}
          size="md"
        />
      }
      categoryLabel={tpl.provider_display_name}
      tags={modelTags}
      comingSoon={comingSoon}
      canInstall={canInstall}
      installing={installing}
      onInstall={onInstall}
      onRequestAccess={onRequestAccess}
      onSuggestPriority={onSuggestPriority}
      upvoteCount={upvoteCount}
      isInstalled={isInstalled}
      onUpvote={onUpvote}
      hasUpvoted={hasUpvoted}
      {...(comingSoon || onOpen === undefined ? {} : { onOpenDetail: onOpen })}
    />
  );
}
